import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Heading, Text } from "@/components/Typography";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useErrorHandler } from "@/context/ErrorHandlerContext";
import { publicApi } from "@/services/publicApi";
import { pabInvitationAccepted } from "@/utils/tracking";

export const AdvisorAcceptancePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const handleApiError = useErrorHandler();

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState<string>("");
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate processing
    if (hasProcessedRef.current) return;

    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setMessage("No token provided");
      return;
    }

    const acceptAdvisorInvitation = async () => {
      hasProcessedRef.current = true;
      try {
        const response = await publicApi.auth.advisorAccept(token);
        setStatus("success");
        setMessage(response.message || "Expert invitation accepted successfully!");

        // Track PAB invitation acceptance
        const respAny = response as any;
        if (respAny?.pabSessionId && respAny?.timeToRespondHours !== undefined) {
          pabInvitationAccepted({
            pab_session_id: respAny.pabSessionId,
            time_to_respond_hours: respAny.timeToRespondHours,
          });
        }

        // Navigate after a short delay to show the success message
        setTimeout(() => {
          if (user) {
            navigate("/home");
          } else {
            navigate("/login");
          }
        }, 2000);
      } catch (error) {
        setStatus("error");
        setMessage("Failed to accept expert invitation. Please try again.");
        handleApiError(error);

        // Navigate after showing error
        setTimeout(() => {
          if (user) {
            navigate("/home");
          } else {
            navigate("/login");
          }
        }, 3000);
      }
    };

    acceptAdvisorInvitation();
  }, [searchParams, navigate, user, handleApiError]);

  const statusDetails = {
    loading: {
      icon: <Loader2 className="w-12 h-12 text-primary-500 animate-spin" />,
      title: "Processing Invitation",
      message: "Please wait while we process your expert invitation...",
    },
    success: {
      icon: <CheckCircle className="w-12 h-12 text-theme-text-success" />,
      title: "Invitation Accepted",
      message: "Redirecting you to your home...",
    },
    error: {
      icon: <XCircle className="w-12 h-12 text-theme-text-error" />,
      title: "Invitation Failed",
      message: "Redirecting you to login...",
    },
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-bg-page">
      <div className="max-w-md w-full mx-auto p-12">
        <div className="bg-theme-bg-surface rounded-lg shadow-lg p-16 text-center">
          <div className="flex justify-center mb-8">{statusDetails[status].icon}</div>

          <Heading as="h1" size="4" className="font-bold mb-4">
            {statusDetails[status].title}
          </Heading>
          <Text className="text-theme-text-secondary mb-12">{message}</Text>
          <Text className="text-sm text-theme-text-muted">{statusDetails[status].message}</Text>
        </div>
      </div>
    </div>
  );
};

export default AdvisorAcceptancePage;
